import React, { useState, useEffect, useCallback } from 'react';
import { Typography, LogoSVG } from 'components/atoms';
import { NewBreadCrumb } from 'components/molecules';
import { ReviewModal } from 'components/organisms/ReviewModal';
import { TaskCard } from 'components/organisms/TaskCard';
import { ViewScreen } from 'components/template';
import './index.scss';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store';
import { getEmployeeGoalAssignments } from 'store/Admin';
import { ApiResponseStatus } from 'models/enums';
import { HRReviewEmployeeGoalAssignment } from 'models/models';

interface Review {
  id: string;
  comment: string;
  rating: number;
  reviewedAt: string;
  reviewedBy: string;
  goalId?: string;
}


interface TransformedGoal {
  id: string;
  title: string;
  description: string;
  priority: 'High' | 'Medium' | 'Low';
  status: 'Completed' | 'Pending' | 'In Progress';
  reviews: Review[];
  assignedTo: string;
  quarter: string;
  subtasks: TransformedSubtask[];
  isParentTask?: boolean;
  combinedGoalId?: string;
  acceptanceStatus?: string | null;
}

interface TransformedSubtask {
  id: string;
  title: string;
  description: string;
  deadline: string;
  status: 'Completed' | 'Pending' | 'In Progress';
  priority: 'High' | 'Medium' | 'Low';
  quarter?: string; 
}

interface Employee {
  id: string;
  name: string;
  position: string;
  department: string;
  email: string;
  joinedDate: string;
  supervisor: string;
  nextReviewDate: string;
  performanceScore: number;
}



const mockEmployees: Employee[] = [
  {
    id: 'EMP123',
    name: 'John Doe',
    position: 'Senior Software Engineer',
    department: 'Engineering',
    email: 'john.doe@aventude.com',
    joinedDate: '2023-01-15',
    supervisor: 'Sarah Mitchell',
    nextReviewDate: '2025-07-25',
    performanceScore: 85
  },
  {
    id: 'EMP124',
    name: 'Jane Smith',
    position: 'UI/UX Designer',
    department: 'Design',
    email: 'jane.smith@aventude.com',
    joinedDate: '2023-03-01',
    supervisor: 'Sarah Mitchell',
    nextReviewDate: '2025-08-05',
    performanceScore: 92
  }
];


const quarters = [
  { label: 'Q1 2025', value: 'Q1' },
  { label: 'Q2 2025', value: 'Q2' },
  { label: 'Q3 2025', value: 'Q3' },
  { label: 'Q4 2025', value: 'Q4' }
];

export const SupervisorReviewTasks = () => {
  const dispatch = useDispatch<AppDispatch>();
  

  const { 
    employeeGoalAssignments, 
    employeeGoalAssignmentsStatus
  } = useSelector((state: RootState) => state.admin);
  

  const [goals, setGoals] = useState<TransformedGoal[]>([]);
  const [selectedGoal, setSelectedGoal] = useState<TransformedGoal | null>(null);
  const [selectedQuarter, setSelectedQuarter] = useState('all');
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [supervisors, setSupervisors] = useState<any[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedPriority, setSelectedPriority] = useState<string>('all');
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  

  const location = useLocation();
  const employeeFromState = location.state?.employee;
  const employeeId = employeeFromState?.userRoleId;


  const transformApiData = useCallback((apiData: HRReviewEmployeeGoalAssignment[]): { goals: TransformedGoal[], employee: Employee, supervisors: any[] } => {
    if (!apiData || apiData.length === 0) {
      return { goals: [], employee: mockEmployees[0], supervisors: [] };
    }

    const assignment = apiData[0];
    if (!assignment) {
      return { goals: [], employee: mockEmployees[0], supervisors: [] };
    }
    
    const supervisors = assignment.supervisors || [];
    

    const employee: Employee = {
      id: employeeFromState?.userRoleId,
      name: employeeFromState?.name,
      position: employeeFromState?.designation,
      department: employeeFromState?.department,
      joinedDate: employeeFromState?.joinedDate,
      nextReviewDate: employeeFromState?.nextReviewDate,
      email: employeeFromState?.personalEmail || employeeFromState?.upn,
      supervisor: supervisors.length > 0 ? supervisors.map((s: any) => s.name).join(', ') : 'N/A',
      performanceScore: employeeFromState?.performanceRate || 85
    };

   


    const transformedGoals: TransformedGoal[] = assignment.goals && assignment.goals.length > 0 
      ? assignment.goals.map(goal => ({
          id: goal.id,
          title: goal.title,
          description: goal.description,
          priority: 'Medium' as const, 
          status: goal.status,
          reviews: [], 
          assignedTo: assignment.employeeId || '',
          quarter: goal.subtasks && goal.subtasks.length > 0 ? goal.subtasks[0].quarter : 'Q1', // Use first subtask's quarter
          acceptanceStatus: goal.acceptanceStatus || null,
          subtasks: goal.subtasks && goal.subtasks.length > 0 
            ? goal.subtasks.map(subtask => ({
                id: subtask.id,
                title: subtask.title,
                description: subtask.description,
                deadline: subtask.deadline,
                status: subtask.status,
                priority: subtask.priority === 'HIGH' ? 'High' : 
                          subtask.priority === 'MEDIUM' ? 'Medium' : 'Low',
                quarter: subtask.quarter 
              }))
            : []
        }))
      : [];

    return { goals: transformedGoals, employee, supervisors };
  }, [employeeFromState]);

  
  useEffect(() => {
    dispatch(getEmployeeGoalAssignments(employeeId));
  }, [dispatch, employeeId]);

  
  useEffect(() => {
    const handleFocus = () => {
      if (employeeId) {
        dispatch(getEmployeeGoalAssignments(employeeId));
      }
    };

    window.addEventListener('focus', handleFocus);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [dispatch, employeeId]);

  
  useEffect(() => {
    if (employeeGoalAssignmentsStatus === ApiResponseStatus.fulfilled && employeeGoalAssignments) {
      const { goals: transformedGoals, employee, supervisors: supervisorsData } = transformApiData(employeeGoalAssignments);
      setGoals(transformedGoals);
      setSelectedEmployee(employee);
      setSupervisors(supervisorsData);
    }
  }, [employeeGoalAssignments, employeeGoalAssignmentsStatus, transformApiData]);

  const handleAddReview = (review: { comment: string; rating: number }) => {
    if (!selectedGoal) return;

    const newReview: Review = {
      id: `review-${Date.now()}`,
      comment: review.comment,
      rating: review.rating,
      reviewedAt: new Date().toISOString(),
      reviewedBy: 'Supervisor Review',
      goalId: selectedGoal.id
    };

    setGoals(prevGoals =>
      prevGoals.map(goal =>
        goal.id === selectedGoal.id
          ? { ...goal, reviews: [...goal.reviews, newReview] }
          : goal
      )
    );

    setIsReviewModalOpen(false);
    setSelectedGoal(null);
  };

  const handleOpenReviewModal = (goal: TransformedGoal) => {
    setSelectedGoal(goal);
    setIsReviewModalOpen(true);
  };

  const filteredGoals = goals.filter(goal => {
    const matchesEmployee = !selectedEmployee || goal.assignedTo === selectedEmployee.id;
    const matchesQuarter = selectedQuarter === 'all' || goal.quarter === selectedQuarter;
    const matchesStatus = selectedStatus === 'all' || goal.status === selectedStatus;
    const matchesPriority = selectedPriority === 'all' || goal.priority === selectedPriority;
    return matchesEmployee && matchesQuarter && matchesStatus && matchesPriority;
  });

  const getGoalsSummary = () => {
    const employeeGoals = goals.filter(g => !selectedEmployee || g.assignedTo === selectedEmployee.id);
    const total = employeeGoals.length;
    const completed = employeeGoals.filter(g => g.status === 'Completed').length;
    const inProgress = employeeGoals.filter(g => g.status === 'In Progress').length;
    const pending = employeeGoals.filter(g => g.status === 'Pending').length;

    const totalSubtasks = employeeGoals.reduce((acc, goal) => acc + goal.subtasks.length, 0);
    const completedSubtasks = employeeGoals.reduce((acc, goal) =>
      acc + goal.subtasks.filter(st => st.status === 'Completed').length, 0);
    const inProgressSubtasks = employeeGoals.reduce((acc, goal) =>
      acc + goal.subtasks.filter(st => st.status === 'In Progress').length, 0);
    const pendingSubtasks = employeeGoals.reduce((acc, goal) =>
      acc + goal.subtasks.filter(st => st.status === 'Pending').length, 0);

    return {
      goals: { total, completed, inProgress, pending },
      subtasks: { total: totalSubtasks, completed: completedSubtasks, inProgress: inProgressSubtasks, pending: pendingSubtasks }
    };
  };

  const summary = getGoalsSummary();

  const headerItem1 = (
    <>
      <div className='hr-review-header'>
        <div className="header-left">
          <Typography type="heading4" text="Supervisor Review Dashboard" />
          <NewBreadCrumb />
        </div>
      </div>
    </>
  );

  const headerItem2 = (
    <>
      <div className="hr-review__filters">
        <div className="filter-group">
          <Typography type="base_medium" text="Quarter" />
          <select
            className="select-input"
            value={selectedQuarter}
            onChange={(e) => setSelectedQuarter(e.target.value)}
          >
            <option value="all">All Quarters</option>
            {quarters.map(quarter => (
              <option key={quarter.value} value={quarter.value}>
                {quarter.label}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <Typography type="base_medium" text="Status" />
          <select
            className="select-input"
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="Completed">Completed</option>
            <option value="In Progress">In Progress</option>
            <option value="Pending">Pending</option>
          </select>
        </div>
        <div className="filter-group">
          <Typography type="base_medium" text="Priority" />
          <select
            className="select-input"
            value={selectedPriority}
            onChange={(e) => setSelectedPriority(e.target.value)}
          >
            <option value="all">All Priorities</option>
            <option value="High">High</option>
            <option value="Medium">Medium</option>
            <option value="Low">Low</option>
          </select>
        </div>
      </div>
    </>
  );

  const isLoading = employeeGoalAssignmentsStatus === ApiResponseStatus.pending;

  if (isLoading) {
    return (
      <div className="loading-container">
        <LogoSVG />
      </div>
    );
  }

  return (
    <ViewScreen
      headerItem1={headerItem1}
      headerItem2={headerItem2}
      mainItem2={
        <>
          <div className="hr-review">
            <div className="hr-review__overview">
              <div className="overview-card employee-supervisor-info">
                <div className="info-header">
                  <Typography type="heading5" text="Employee & Supervisor Information" />
                </div>
                <div className="combined-info-content">
                  <div className="info-section">
                    <div className="section-title">
                      <Typography type="small_bold" text="Employee" />
                    </div>
                    <div className="info-content">
                      <div className="info-row">
                        <Typography type="base_medium" text="Name:" />
                        <Typography type="base_regular" text={selectedEmployee?.name || 'Loading...'} />
                      </div>
                      <div className="info-row">
                        <Typography type="base_medium" text="Email:" />
                        <Typography type="base_regular" text={selectedEmployee?.email || 'Loading...'} />
                      </div>
                      <div className="info-row">
                        <Typography type="base_medium" text="Title:" />
                        <Typography type="base_regular" text={selectedEmployee?.position || 'Loading...'} />
                      </div>
                    </div>
                  </div>

                  <div className="info-divider"></div>

                  <div className="info-section">
                    <div className="section-title">
                      <Typography type="small_bold" text="Supervisors" />
                    </div>
                    <div className="info-content">
                      {supervisors && supervisors.length > 0 ? (
                        supervisors.map((supervisor, index) => (
                          <div key={supervisor.id || index} className="supervisor-item">
                            <div className="supervisor-details">
                              <div className="info-row">
                                <Typography type="base_medium" text="Name:" />
                                <Typography type="base_regular" text={supervisor?.name || 'Loading...'} />
                              </div>
                              <div className="info-row">
                                <Typography type="base_medium" text="Email:" />
                                <Typography type="base_regular" text={supervisor?.email || 'Loading...'} />
                              </div>
                              <div className="info-row">
                                <Typography type="base_medium" text="Title:" />
                                <Typography type="base_regular" text={supervisor?.designation || 'Loading...'} />
                              </div>
                            </div>
                            {index < supervisors.length - 1 && <div className="supervisor-divider"></div>}
                          </div>
                        ))
                      ) : (
                        <div className="info-row">
                          <Typography type="base_regular" text="No supervisors assigned" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="overview-card combined-stats">
                <div className="stats-header">
                  <Typography type="heading5" text="Goals & Subtasks Overview" />
                </div>
                <div className="combined-stats-content">
                  <div className="stats-section">
                    <div className="section-title">
                      <Typography type="small_bold" text="Goals" />
                    </div>
                    <div className="stats-row">
                      <div className="stat-item">
                        <Typography type="small_medium" text="Total" />
                        <Typography type="heading5" text={summary.goals.total.toString()} />
                      </div>
                      <div className="stat-item">
                        <Typography type="small_medium" text="Completed" />
                        <Typography type="heading5" text={summary.goals.completed.toString()} />
                      </div>
                      <div className="stat-item">
                        <Typography type="small_medium" text="In Progress" />
                        <Typography type="heading5" text={summary.goals.inProgress.toString()} />
                      </div>
                      <div className="stat-item">
                        <Typography type="small_medium" text="Pending" />
                        <Typography type="heading5" text={summary.goals.pending.toString()} />
                      </div>
                    </div>
                  </div>
                  
                  <div className="stats-divider"></div>
                  
                  <div className="stats-section">
                    <div className="section-title">
                      <Typography type="small_bold" text="Subtasks" />
                    </div>
                    <div className="stats-row">
                      <div className="stat-item">
                        <Typography type="small_medium" text="Total" />
                        <Typography type="heading5" text={summary.subtasks.total.toString()} />
                      </div>
                      <div className="stat-item">
                        <Typography type="small_medium" text="Completed" />
                        <Typography type="heading5" text={summary.subtasks.completed.toString()} />
                      </div>
                      <div className="stat-item">
                        <Typography type="small_medium" text="In Progress" />
                        <Typography type="heading5" text={summary.subtasks.inProgress.toString()} />
                      </div>
                      <div className="stat-item">
                        <Typography type="small_medium" text="Pending" />
                        <Typography type="heading5" text={summary.subtasks.pending.toString()} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="hr-review__content">
              <div className="goals-grid">
                {filteredGoals.map(goal => (
                  <TaskCard
                    key={goal.id}
                    task={goal}
                    onCardClick={() => handleOpenReviewModal(goal)}
                  />
                ))}
                
              </div>
            </div>
          </div>

          <ReviewModal
            isOpen={isReviewModalOpen}
            onClose={() => {
              setIsReviewModalOpen(false);
              setSelectedGoal(null);
             
              if (employeeId) {
                dispatch(getEmployeeGoalAssignments(employeeId));
              }
            }}
            task={selectedGoal}
            onSubmit={handleAddReview}
            goalAssignmentGoalId={selectedGoal?.id}
            reviewerRole="Supervisor"
          />
        </>
      }
      portalTitle="Supervisor Review"
    />
  );
}; 